/* rl-fw-fallback — covers the Refind Live hero when Firework renders no live content.

   The "live" hero hands its whole surface to <fw-herounit>. When the configured livestream
   playlist has no active/upcoming video — the stream isn't in the playlist yet (pre-show),
   or it's been moved to the archive but the drop is still flagged "live" (post-show) — the
   element upgrades to full height but renders nothing, so the visitor sees the section's
   black backdrop (a "big black screen"). See FIREWORK.md.

   DESIGN: the ground truth is the DOM — "did the herounit render any content?" — checked as
   an empty shadow root (childElementCount === 0). Firework's `fw:widget:render-outcome`
   event is used only as a fast, precise TRIGGER: it fires when the render attempt resolves
   (empty feed → {type:'herounit', status:'error', reason:'No configs returned a valid
   feed'}), letting us react immediately instead of guessing with a timer. So:
     - status 'error'  → empty feed, reveal the fallback now.
     - any other status → stand down ONLY if the DOM actually has content (`!isEmpty`).
   A single defensive timeout is the floor: if no outcome ever settles the hero (SDK blocked,
   event renamed, or an empty feed reported under some non-'error' status), it reveals the
   fallback when — and only when — the shadow root is genuinely empty. That keeps the
   undocumented event as an optimization, not a load-bearing dependency: if Firework ever
   changes it, detection degrades to the DOM check (slower) rather than breaking. These
   `fw:*` events are undocumented but verified live; FIREWORK.md records the payloads.

   Recovery is a manual reload ("Check again"): the SDK caches the playlist in memory for the
   page's lifetime, so a stream added after load only appears on a fresh load. */

(function () {
  var DEFENSIVE_MS = 15000; // no conclusive outcome AND still empty after this → reveal fallback

  function isEmpty(fw) {
    var sr = fw.shadowRoot;
    return !sr || sr.childElementCount === 0;
  }

  function liveSection(target) {
    var s = target && target.closest ? target.closest('.rl-hero--live') : null;
    return s || document.querySelector('.rl-hero--live');
  }

  // One document-level listener, bound at module init — BEFORE any hero swap or SDK render
  // (the script is deferred and the SDK loads async after) — so an outcome can never fire
  // into an attach-window gap. render-outcome fires on the herounit element AND on document;
  // either reaches us here.
  document.addEventListener('fw:widget:render-outcome', function (e) {
    var d = e && e.detail;
    if (!d || d.type !== 'herounit') return; // ignore the archive embed-feed's outcome
    var section = liveSection(e.target);
    if (!section) return;
    var fw = section.querySelector('fw-herounit');
    var fallback = section.querySelector('[data-rl-fw-fallback]');
    if (!fw || !fallback) return;

    if (d.status === 'error') {
      section.__rlFwSettled = true;
      fallback.classList.add('is-shown');
    } else if (!isEmpty(fw)) {
      // Non-error AND content is actually present → the stream is up. Don't stand down on
      // status alone: an empty feed reported under a non-'error' status must still be caught
      // by the defensive DOM check below.
      section.__rlFwSettled = true;
      fallback.classList.remove('is-shown');
    }
  });

  function mount(section) {
    var fw = section.querySelector('fw-herounit');
    var fallback = section.querySelector('[data-rl-fw-fallback]');
    if (!fw || !fallback || section.__rlFwTimer) return;

    var refresh = fallback.querySelector('[data-rl-fw-refresh]');
    if (refresh && !refresh.__rlBound) {
      refresh.__rlBound = true;
      refresh.addEventListener('click', function (e) { e.preventDefault(); location.reload(); });
    }

    // The floor. Generous so a genuinely slow feed reports its outcome (and paints) first.
    section.__rlFwTimer = setTimeout(function () {
      if (!section.__rlFwSettled && isEmpty(fw)) fallback.classList.add('is-shown');
    }, DEFENSIVE_MS);
  }

  function unmount(section) {
    if (section.__rlFwTimer) { clearTimeout(section.__rlFwTimer); section.__rlFwTimer = null; }
    section.__rlFwSettled = false;
  }

  function mountAll(scope) {
    (scope || document).querySelectorAll('.rl-hero--live').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mountAll(); });
  } else {
    mountAll();
  }

  // rl-live-poll swaps the live hero in for a visitor who was waiting on the upcoming state;
  // that swap doesn't fire shopify:section:load, so it announces itself here.
  document.addEventListener('rl:live-swapped', function () { mountAll(); });

  // Theme editor: re-bind / clean up across section reloads.
  document.addEventListener('shopify:section:load', function (e) { mountAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('.rl-hero--live').forEach(unmount);
  });
})();
